// SPDX-License-Identifier: AGPL-3.0-only
/**
 * PageRenderer — the runtime config interpreter:
 *
 *   GET /api/v1/pages/:pageId → migrate + Zod-validate (src/api/pages.ts)
 *     → resolve `template` against the page-template registry
 *     → mount the template with its data adapters (CrudApi / DashboardData)
 *     → WidgetEvents: record-open → hrefForRecord navigation, drill-through →
 *       href navigation, mutate → CRUD call + undo toast + cache invalidation.
 *
 * Never-crash rules: unknown slug → branded 404 (no server round trip); `v`
 * too new → "config too new" card; invalid envelope → invalid-config card;
 * unknown/not-yet-shipped template → unknown-template card; a throwing
 * template degrades to an error card via WidgetErrorBoundary. Page-level API
 * 403/404/5xx render the matching system state *inside* the content outlet —
 * shell and nav stay usable.
 */
import { useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { useParams, useRouter } from '@tanstack/react-router';
import { FileQuestion, PackageOpen, ShieldAlert } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Card, CardBody, CardHeader, IconTile, MonoText, Skeleton } from '@adminium/ui';
import { WidgetErrorBoundary, isDeletePreview, type ColumnFacts, type WidgetEvent } from '@adminium/widgets';
import type { PageEnvelope } from '@adminium/engine/config';

import { createCrudApi, type BoundCrudApi, type CrudMutationResult } from '../api/crud.js';
import {
  pageQuery,
  type FormChildFactReply,
  type FormColumnFactReply,
  type FormRelationFactReply,
} from '../api/pages.js';
import { useDashboardData } from '../api/widgetData.js';
import { bootstrapQuery, findPageBySlug, slugForTable } from '../app/bootstrap.js';
import { hrefForPage, hrefForRecord } from '../app/links.js';
import { requestIdForError, stateIdForError } from '../app/query.js';
import { t } from '../i18n/t.js';
import {
  PageSurface,
  resolvePagePadding,
  resolvePageWidth,
  type PagePadding,
  type PageSurfaceWidth,
} from '../shell/PageSurface.js';
import { NotFoundPage } from '../states/NotFoundPage.js';
import { StatePage } from '../states/StatePage.js';
import { PageHostContext, type PageHost } from './pageHost.js';
import { DEFAULT_TEMPLATE_SURFACE, templateSurface } from './surfaceDefaults.js';
import { resolvePageTemplate, type PageTemplateAdapters, type PageTemplateComponent } from './templates.js';
import { useUndoToast } from './toasts.js';

export function PageRenderer() {
  const params = useParams({ strict: false });
  const slug = typeof params.slug === 'string' ? params.slug : '';
  const recordId = typeof params.recordId === 'string' ? params.recordId : undefined;
  const { data: bootstrap } = useSuspenseQuery(bootstrapQuery());
  // Resolution consults hidden pages too — a page hidden from the sidebar
  // still answers its URL (30 follow-up).
  const item = findPageBySlug(bootstrap, slug);

  // Unknown slug → branded 404 with NO pages round trip.
  if (item === null) return <NotFoundPage />;
  return <PageDocument key={item.pageId} pageId={item.pageId} slug={slug} recordId={recordId} />;
}

function PageDocument({ pageId, slug, recordId }: { pageId: string; slug: string; recordId?: string | undefined }) {
  const page = useQuery(pageQuery(pageId));

  // No envelope yet, so no template and no width to match — `page` is what this
  // skeleton has always used. Once TemplateMount knows the template it renders
  // its own skeleton in the real column instead.
  if (page.isPending) return <PageSkeleton width="page" />;
  if (page.isError) {
    // Page-scoped failure: system state inside the outlet, shell stays usable.
    return (
      <StatePage
        stateId={stateIdForError(page.error)}
        requestId={requestIdForError(page.error)}
        fullPage={false}
        onRetry={() => void page.refetch()}
      />
    );
  }

  const result = page.data;
  if (result.status === 'too-new') {
    return (
      <PageMessageCard
        icon={<ShieldAlert />}
        title={t('page.tooNew.title', 'This page needs a newer Adminium')}
        body={t(
          'page.tooNew.body',
          'This page was saved with config version {version}, but this build understands up to version {latest}. Upgrade Adminium to open it.',
          { version: String(result.v), latest: String(result.latest) },
        )}
        detail={pageId}
      />
    );
  }
  if (result.status === 'invalid') {
    return (
      <PageMessageCard
        icon={<ShieldAlert />}
        title={t('page.invalid.title', 'This page’s configuration is invalid')}
        body={t('page.invalid.body', 'The stored page document failed validation and cannot be rendered.')}
        detail={result.issues.slice(0, 5).join('\n')}
      />
    );
  }

  return (
    <TemplateMount
      page={result.page}
      slug={slug}
      recordId={recordId}
      canEditLayout={result.canEditLayout}
      canCreate={result.canCreate}
      canUpdate={result.canUpdate}
      canDelete={result.canDelete}
      canAttach={result.canAttach}
      canUnmask={result.canUnmask}
      columnFacts={result.columnFacts}
      formColumns={result.formColumns}
      formRelations={result.formRelations}
      formChildren={result.formChildren}
      tableLabelSingular={result.tableLabelSingular}
    />
  );
}

type TemplateResolution =
  | { phase: 'resolving' }
  | { phase: 'resolved'; component: PageTemplateComponent | null }
  | { phase: 'failed'; error: Error };

/**
 * The template id the record child route renders: with a `recordId` present,
 * a page envelope whose `config.detail.template` names one owns the route —
 * today always `page-record` in generated crud bodies. Envelopes without a
 * `detail` block keep the page's own template byte-for-byte (which is what
 * leaves `page-master-detail`, `page-board` and every non-crud template
 * untouched).
 *
 * A shallow read, deliberately — the full typed parse happens inside the lazy
 * record binding; the entry chunk only needs the id string.
 */
function detailTemplateOf(page: PageEnvelope): string | null {
  const detail = page.config['detail'];
  if (typeof detail !== 'object' || detail === null) return null;
  const template = (detail as { template?: unknown }).template;
  return typeof template === 'string' && template.length > 0 ? template : null;
}

/** Mounts one page document with its template. Exported for its tests. */
export function TemplateMount({
  page,
  slug,
  recordId,
  canEditLayout,
  canCreate,
  canUpdate,
  canDelete,
  canAttach,
  canUnmask,
  columnFacts,
  formColumns,
  formRelations,
  formChildren,
  tableLabelSingular,
}: {
  page: PageEnvelope;
  slug: string;
  recordId?: string | undefined;
  canEditLayout?: boolean | undefined;
  canCreate?: boolean | undefined;
  canUpdate?: boolean | undefined;
  canDelete?: boolean | undefined;
  canAttach?: boolean | undefined;
  canUnmask?: boolean | undefined;
  columnFacts?: ColumnFacts | undefined;
  formColumns?: readonly FormColumnFactReply[] | undefined;
  formRelations?: readonly FormRelationFactReply[] | undefined;
  formChildren?: readonly FormChildFactReply[] | undefined;
  tableLabelSingular?: string | null | undefined;
}) {
  const [resolution, setResolution] = useState<TemplateResolution>({ phase: 'resolving' });
  const [attempt, setAttempt] = useState(0);

  // The effective template — the detail template on the record route, the
  // page's own everywhere else. Same registry, same memoized resolution,
  // same unknown-template card.
  const effectiveTemplate =
    recordId !== undefined ? (detailTemplateOf(page) ?? page.template) : page.template;

  useEffect(() => {
    let alive = true;
    setResolution({ phase: 'resolving' });
    // A chunk that fails to load gets the error card and its Retry, not a
    // skeleton that never ends. `resolvePageTemplate` forgets a failed load, so
    // Retry (a new `attempt`) fetches it again.
    resolvePageTemplate(effectiveTemplate).then(
      (component) => {
        if (alive) setResolution({ phase: 'resolved', component });
      },
      (error: unknown) => {
        if (alive) {
          setResolution({ phase: 'failed', error: error instanceof Error ? error : new Error(String(error)) });
        }
      },
    );
    return () => {
      alive = false;
    };
  }, [effectiveTemplate, attempt]);

  const adapters = usePageAdapters(page, slug);

  // The connection's display currency, resolved once here so every template
  // gets it the same way and no binding has to know about the nav tree
  // . `useQuery` rather than the suspense form:
  // this is a formatting nicety, and a page must render without it.
  const { data: navBootstrap } = useQuery(bootstrapQuery());
  const currency =
    navBootstrap === undefined ? null : (findPageBySlug(navBootstrap, slug)?.currency ?? null);

  // The ONE gutter for `/p/<slug>`: the template's default from
  // `surfaceDefaults`, overridden by the page's stored `padding` when an admin
  // set one. Applied here rather than in each of the fifteen bindings so the
  // skeleton, the error cards and the loaded template all sit in the same box —
  // previously the skeleton had a gutter and ten of the templates had none, so
  // the page jumped as it loaded.
  const surface = templateSurface(effectiveTemplate);
  const padding = resolvePagePadding(page.padding, surface.padding);
  const width = resolvePageWidth(page.width, surface.width);

  if (resolution.phase === 'resolving') {
    return <PageSkeleton padding={padding} width={width} />;
  }
  // A template whose code did not load renders as one that throws, so the
  // boundary below shows it, and its Retry resolves the template again.
  const failed = resolution.phase === 'failed' ? resolution.error : null;
  const Template: PageTemplateComponent | null =
    resolution.phase === 'resolved'
      ? resolution.component
      : () => {
          throw failed;
        };
  if (Template === null) {
    // Unknown template id — forward compatibility: degrade, never crash.
    return (
      <PageMessageCard
        icon={<FileQuestion />}
        title={t('page.unknownTemplate.title', 'Unknown page template')}
        body={t(
          'page.unknownTemplate.body',
          'This page uses a template this build doesn’t recognize. It may come from a newer Adminium or an extension that isn’t installed.',
        )}
        detail={effectiveTemplate}
      />
    );
  }

  return (
    <WidgetErrorBoundary
      fallback={(error, reset) => (
        <PageMessageCard
          icon={<PackageOpen />}
          title={t('page.renderError.title', 'This page failed to render')}
          body={error.message}
          detail={effectiveTemplate}
          action={
            <button
              type="button"
              className="text-body-sm font-bold text-accent hover:underline"
              onClick={failed === null ? reset : () => setAttempt((value) => value + 1)}
            >
              {t('common.retry', 'Retry')}
            </button>
          }
        />
      )}
    >
      <PageSurface padding={padding} width={width} fill={surface.fill}>
        <PageHostContext.Provider value={PAGE_HOST}>
          <Template
            page={page}
            adapters={adapters}
            recordId={recordId}
            canEditLayout={canEditLayout}
            canCreate={canCreate}
            canUpdate={canUpdate}
            canDelete={canDelete}
            canAttach={canAttach}
            canUnmask={canUnmask}
            {...(columnFacts === undefined ? {} : { columnFacts })}
            {...(formColumns === undefined ? {} : { formColumns })}
            {...(formRelations === undefined ? {} : { formRelations })}
            {...(formChildren === undefined ? {} : { formChildren })}
            {...(tableLabelSingular === undefined ? {} : { tableLabelSingular })}
            {...(currency === null || currency === undefined ? {} : { currency })}
          />
        </PageHostContext.Provider>
      </PageSurface>
    </WidgetErrorBoundary>
  );
}

/** Data adapters + the WidgetEvent sink for one mounted page. */
function usePageAdapters(page: PageEnvelope, slug: string): PageTemplateAdapters {
  const router = useRouter();
  const queryClient = useQueryClient();
  const notifyUndoable = useUndoToast();
  const { data: bootstrap } = useSuspenseQuery(bootstrapQuery());

  const crud: BoundCrudApi | null = useMemo(() => {
    const { connectionId, table } = page.source;
    return connectionId !== null && table !== null ? createCrudApi(connectionId, table) : null;
  }, [page.source]);

  const dashboard = useDashboardData(page);
  const hasLayout = page.kind === 'dashboard';

  const openRecord = useCallback(
    (recordId: string | null) => {
      router.history.push(recordId === null ? hrefForPage(slug) : hrefForRecord(slug, recordId));
    },
    [router, slug],
  );

  /**
   * `record-open` routing: the event names a TABLE, and the record page that
   * shows it may not be this one — a related-tab row or an FK chip points at
   * another table entirely. Resolve the table to its page through the
   * bootstrap map; the page's own table keeps today's same-slug path. A
   * table no page shows is a no-op rather than a wrong-page navigation (the
   * old behavior pushed the OTHER table's id onto THIS page's record route).
   */
  const openRecordFor = useCallback(
    (event: { connectionId?: string | undefined; table: string; recordId: string | number }) => {
      const recordId = String(event.recordId);
      const ownTable = page.source.table;
      if (event.table === ownTable) {
        openRecord(recordId);
        return;
      }
      const targetSlug = slugForTable(
        bootstrap,
        event.connectionId ?? page.source.connectionId,
        event.table,
      );
      if (targetSlug !== null) router.history.push(hrefForRecord(targetSlug, recordId));
    },
    [openRecord, page.source.table, page.source.connectionId, bootstrap, router],
  );

  const invalidateAfterMutation = useCallback(() => {
    if (crud !== null) {
      void queryClient.invalidateQueries({ queryKey: ['data', crud.connectionId, crud.table] });
    }
    void queryClient.invalidateQueries({ queryKey: ['widget-data'] });
  }, [queryClient, crud]);

  const onEvent = useCallback(
    (event: WidgetEvent) => {
      if (event.type === 'drill-through') {
        router.history.push(event.href);
        return;
      }
      if (event.type === 'record-open') {
        // Widgets never navigate directly — the host maps record-open to the
        // canonical record href (navigation helpers), routed by the
        // event's TABLE.
        openRecordFor(event);
        return;
      }
      // mutate intent: run through the CRUD API with undo + audit.
      if (crud === null) return;
      const run = async (): Promise<CrudMutationResult> => {
        if (event.intent === 'insert') return crud.create(event.values ?? {});
        if (event.intent === 'update') return crud.update(String(event.recordId), event.values ?? {});
        const deleted = await crud.remove(String(event.recordId), { confirm: true });
        return isDeletePreview(deleted) ? { data: null, undoToken: null } : deleted;
      };
      // Return the promise so optimistic widgets (kanban boards) can roll back
      // a rejected move; the host's own toast/invalidation chain runs alongside.
      const promise = run();
      promise
        .then((result) => {
          invalidateAfterMutation();
          notifyUndoable({
            title:
              event.intent === 'insert'
                ? t('mutation.created', 'Record created')
                : event.intent === 'update'
                  ? t('mutation.updated', 'Record updated')
                  : t('mutation.deleted', 'Record deleted'),
            undoToken: result.undoToken,
            onUndone: invalidateAfterMutation,
          });
        })
        .catch(() => {
          // Failed mutations surface through the template's own form/error
          // handling; the host-level fallback stays silent here.
        });
      return promise;
    },
    [router, openRecordFor, crud, invalidateAfterMutation, notifyUndoable],
  );

  return useMemo<PageTemplateAdapters>(
    () => ({ crud, dashboard: hasLayout ? dashboard : null, onEvent, openRecord, notifyUndoable }),
    [crud, hasLayout, dashboard, onEvent, openRecord, notifyUndoable],
  );
}

/** What a template's own mounts use (`pageHost.ts`); exported for tests. */
export const PAGE_HOST: PageHost = { usePageAdapters, resolvePageTemplate, detailTemplateOf };

// --- chrome ------------------------------------------------------------------

/**
 * Sits in the same box the loaded page will: `TemplateMount` knows the
 * template by the time it renders this, so the swap is content appearing in
 * place rather than the whole page re-flowing. `PageDocument` renders it before
 * the envelope arrives and has to fall back to the shared default.
 */
function PageSkeleton({ padding, width }: { padding?: PagePadding; width?: PageSurfaceWidth }) {
  return (
    <PageSurface
      padding={padding ?? DEFAULT_TEMPLATE_SURFACE.padding}
      width={width ?? DEFAULT_TEMPLATE_SURFACE.width}
    >
      <div className="flex flex-col gap-4" data-testid="page-skeleton">
        <Skeleton className="h-7 w-1/3" />
        <Skeleton className="h-4 w-1/2" />
        <Skeleton className="h-64 w-full" />
      </div>
    </PageSurface>
  );
}

function PageMessageCard({
  icon,
  title,
  body,
  detail,
  action,
}: {
  icon: ReactNode;
  title: string;
  body: string;
  detail?: string | undefined;
  action?: ReactNode;
}) {
  return (
    <PageSurface width="page">
      <Card>
        <CardHeader className="flex items-center gap-3">
          <IconTile tone="warn" size="md" icon={icon} />
          <h2 className="min-w-0 truncate text-section text-fg">{title}</h2>
        </CardHeader>
        <CardBody className="flex flex-col gap-3">
          <p className="whitespace-pre-line text-body-sm text-fg-muted">{body}</p>
          {detail === undefined ? null : (
            <MonoText className="text-caption text-fg-subtle">{detail}</MonoText>
          )}
          {action}
        </CardBody>
      </Card>
    </PageSurface>
  );
}
